import React from 'react'

/**
 * The last thing between a thrown render error and a white page.
 *
 * A8: a payment page that goes blank after the card was submitted is the worst possible
 * failure. The payer does not know whether they have been charged, so they either try again
 * -- paying twice -- or phone the business, who cannot tell them either.
 *
 * Deliberately plain: no MUI, no theme, no i18next. Those are exactly the things that may
 * have thrown, and a boundary that can itself fail is not a boundary. Its two languages are
 * written here, picked by <html lang> (App sets it from src/lang.js); anything else is English.
 */
const TEXT = {
  he: {
    dir: 'rtl',
    title: 'משהו השתבש',
    charged: <>הכרטיס שלך <strong>לא</strong> חויב. נא לטעון את הדף מחדש ולנסות שוב.</>,
    call: (phone) => `אם זה חוזר על עצמו, התקשרו ל-${phone}.`,
  },
  en: {
    dir: 'ltr',
    title: 'Something went wrong',
    charged: <>Your card has <strong>not</strong> been charged. Please reload this page and try again.</>,
    call: (phone) => `If it keeps happening, call ${phone}.`,
  },
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { failed: false }
  }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error, info) {
    // No console.log of anything payment-shaped; this is the component tree, not the payload.
    if (window && window.console) {
      window.console.error('pay page render failed', error, info)
    }
  }

  render() {
    if (!this.state.failed) {
      return this.props.children
    }

    const text = (document.documentElement.lang || '').toLowerCase().startsWith('he') ? TEXT.he : TEXT.en

    return (
      <div
        dir={text.dir}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
          maxWidth: 420,
          margin: '48px auto',
          padding: '0 16px',
          textAlign: 'center',
          color: '#1f2933',
        }}
      >
        <h2 style={{ fontSize: 20, marginBottom: 8 }}>{text.title}</h2>
        {/* The sentence that matters. Everything else on this screen is secondary. */}
        <p style={{ margin: '0 0 16px', lineHeight: 1.5 }}>
          {text.charged}
        </p>
        {this.props.phone && (
          <p style={{ margin: 0, color: '#52606d' }}>
            {text.call(this.props.phone)}
          </p>
        )}
      </div>
    )
  }
}
